/**
 * SRP-specific WOW helpers — variant checks and progressive fetch.
 * Enablement is unified in `@/utils/wowPricing` (`wow_price_ct_dt`).
 */

import { getCookie } from '@/utils/general/cookie'
import { isEmpty } from '@/utils/general/browserHelper'
import { isServer } from '@/utils/general'
import {
    getWowPricingVariant,
    isWowPricingEnabled,
    isWowTest2Variant
} from '@/utils/wowPricing'

export function collapseSamePriceCards<T extends {
    cardId?: string | number
    travelOptionId?: string
    samePriceCards?: Array<string | number>
}>(cards: T[] = []): T[] {
    const samePriceIds: Array<string | number> = []
    return cards.filter((card: T = {} as T) => {
        if (isEmpty(card)) return false
        const isSamePrice = samePriceIds.some(
            id =>
                id != null &&
                (id == card?.cardId ||
                    (card?.travelOptionId && id === card?.travelOptionId))
        )
        if (!isSamePrice && Array.isArray(card?.samePriceCards) && card.samePriceCards.length > 0) {
            samePriceIds.push(...card.samePriceCards)
        }
        return !isSamePrice
    })
}

/** Cleartrip-only: international trip + Statsig variant `test2`. */
export const isWowTest2CleartripVariant = (
    wowPricingVariant: string = '',
    isIntl: boolean = false
): boolean => isIntl && isWowTest2Variant(wowPricingVariant)

/** Domestic journey + Statsig variant `test`. */
export const isWowCtDomTestVariant = (
    wowPricingVariant: string = '',
    isIntl: boolean = false
): boolean => !isIntl && getWowPricingVariant(wowPricingVariant) === 'test'

/**
 * Progressive WOW pricing — batches of 10 after the initial 100 cards.
 * Enabled when initial WOW is on and the user is signed in.
 */
export const isProgressiveWowEnabled = (
    wowPricingVariant: string = '',
    isIntl: boolean = false,
    isUserSignedIn: boolean = false
): boolean => {
    if (!isUserSignedIn) return false
    if (isServer()) return false
    if (getCookie('wowPriceExp')) return true
    return isWowPricingEnabled(wowPricingVariant, !isIntl)
}
